import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import events from '../data/events';

const { FiArrowLeft, FiArrowRight } = FiIcons;

function EventReel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef(null);
  const reduceMotion = useReducedMotion();

  const move = (direction) => {
    setActive((current) => (current + direction + events.length) % events.length);
  };

  useEffect(() => {
    events.slice(1).forEach((event) => {
      const image = new Image();
      image.src = event.image;
    });
  }, []);

  useEffect(() => {
    if (paused || reduceMotion) return undefined;
    const timer = window.setInterval(() => move(1), 4500);
    return () => window.clearInterval(timer);
  }, [paused, reduceMotion]);

  const handleTouchEnd = (event) => {
    if (touchStart.current === null) return;
    const distance = event.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(distance) > 45) move(distance > 0 ? -1 : 1);
    touchStart.current = null;
  };

  const event = events[active];

  return (
    <section
      className="event-reel"
      aria-labelledby="event-reel-title"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => !e.currentTarget.contains(e.relatedTarget) && setPaused(false)}
      onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
      onTouchEnd={handleTouchEnd}
    >
      <h2 id="event-reel-title" className="reel-heading">
        Rooms Your Membership Helps Make Possible
      </h2>
      <div className="reel-orbit reel-orbit-one" />
      <div className="reel-orbit reel-orbit-two" />
      <div className="reel-stage">
        <div className="scan-lines" />
        <AnimatePresence initial={false}>
          <motion.article
            key={event.title}
            className="event-poster"
            initial={reduceMotion ? false : { opacity: 0, rotateY: 18, scale: 0.92, x: 40 }}
            animate={{ opacity: 1, rotateY: 0, scale: 1, x: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, rotateY: -16, scale: 0.94, x: -30 }}
            transition={{ duration: 0.58, ease: [0.22, 1, 0.36, 1] }}
            style={{ '--event-accent': event.accent }}
          >
            <img
              src={event.image}
              alt=""
              draggable="false"
              width="960"
              height="540"
              fetchPriority={active === 0 ? 'high' : 'auto'}
            />
            <div className="poster-copy">
              <span>{event.label}</span>
              <h3>{event.title}</h3>
              <p>{event.subtitle}</p>
            </div>
          </motion.article>
        </AnimatePresence>
      </div>

      <p className="sr-only" aria-live="polite" aria-atomic="true">
        Showing {event.title}: {event.subtitle}. Slide {active + 1} of{' '}
        {events.length}.
      </p>

      <div className="reel-footer">
        <div className="reel-progress" aria-label={`Event ${active + 1} of ${events.length}`}>
          {events.map((item, index) => (
            <button
              type="button"
              key={item.title}
              className={index === active ? 'progress-dot active' : 'progress-dot'}
              onClick={() => setActive(index)}
              aria-label={`Show ${item.title}`}
              aria-current={index === active ? 'true' : undefined}
            />
          ))}
        </div>
        <div className="reel-controls">
          <button type="button" onClick={() => move(-1)} aria-label="Previous event">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <button type="button" onClick={() => move(1)} aria-label="Next event">
            <SafeIcon icon={FiArrowRight} />
          </button>
        </div>
      </div>
    </section>
  );
}

export default EventReel;